import { useNavigate } from 'react-router-dom'
import type { ProjectListItem } from '../types'
import { buildImageUrl } from '../utils/buildImageUrl'
import { usePlanPreview } from './usePlanPreview'
import styles from './ProjectCard.module.css'

interface ProjectCardProps {
  project: ProjectListItem
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  validated: 'Validado',
  generated: 'Generado',
}

const STATUS_CLASS: Record<string, string> = {
  draft: 'draft',
  validated: 'validated',
  generated: 'generated',
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-CL', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

export function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate()
  const statusKey = project.status?.toLowerCase() ?? 'draft'
  const hasWorkspace = project.room_count > 0
  const { url: previewUrl, loading: previewLoading } = usePlanPreview(project.id, hasWorkspace)
  const thumbnailSrc = hasWorkspace ? previewUrl : (project.thumbnail_url ? buildImageUrl(project.thumbnail_url) : null)

  const handleClick = () => navigate(`/projects/${project.id}`)

  return (
    <article
      className={styles.card}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      aria-label={`Ver proyecto ${project.name}`}
    >
      {(thumbnailSrc || (hasWorkspace && previewLoading)) && (
        <div className={styles.thumbnail}>
          {thumbnailSrc
            ? <img src={thumbnailSrc} alt={`Plano de ${project.name}`} className={styles.thumbnailImg} loading="lazy" />
            : <div className={styles.thumbnailSkeleton} />
          }
        </div>
      )}
      <div className={styles.content}>
      <div className={styles.top}>
        <h2 className={styles.name}>{project.name}</h2>
        <span className={`${styles.badge} ${styles[STATUS_CLASS[statusKey] ?? 'draft']}`}>
          {STATUS_LABELS[statusKey] ?? project.status}
        </span>
      </div>
      <div className={styles.meta}>
        {project.commune && (
          <span className={styles.metaItem}>{project.commune}</span>
        )}
        {project.square_meters && (
          <span className={styles.metaItem}>{project.square_meters} m²</span>
        )}
        {project.room_count > 0 && (
          <span className={styles.metaItem}>{project.room_count} recinto{project.room_count !== 1 ? 's' : ''}</span>
        )}
      </div>
      <div className={styles.footer}>
        <span className={styles.date}>{formatDate(project.created_at)}</span>
        <span className={styles.cta}>Ver proyecto →</span>
      </div>
      </div>
    </article>
  )
}
